import { isNativeApp } from '@/native/shims';

/**
 * network.ts — public online config (NO secrets; a WebSocket URL is public by
 * design, like a website address). The deployed Cloudflare referee is the default
 * host for `?net=ws`; override per-launch with `?host=ws://localhost:8787` to hit
 * the local Node host, or set VITE_WORKER_WSS_URL at build time.
 */
export const networkConfig = {
  /** The live Cloudflare worker — ALWAYS the matchmaker host (V2: rooms may
   *  live elsewhere, matchmaking stays here), and the room-host chain's
   *  Cloudflare entry. */
  wsUrl:
    import.meta.env.VITE_WORKER_WSS_URL ?? 'wss://dribble-racer-host.anthonyfiume09.workers.dev',
};

/**
 * M108b — may this page honour a RAW host URL from the query string
 * (`?host=`, `?wt=`, `?wthash=`)? Those point the client at an arbitrary
 * referee, so the M100 rule says a crafted link must never enable them for a
 * real player. They were gated on `?debug=1` — which is itself just a URL
 * param an attacker appends, so the gate was decorative. Now they require a
 * DEV ORIGIN, which a shared dribbleracer.com link can never satisfy.
 * (The allowlisted `?h=` key and the room code's region letter are
 * unaffected — those can only ever resolve to a host in ROOM_HOSTS.)
 */
export function devHostOverrideAllowed(): boolean {
  // M111: the Capacitor app's origin IS localhost (capacitor://localhost on
  // iOS, https://localhost on Android) — without this check every SHIPPED
  // native build would count as a dev origin and silently re-open the raw
  // host overrides M108b closed. Checked FIRST, before the vite-dev case.
  if (isNativeApp()) return false;
  if (import.meta.env.DEV) return true; // vite dev server
  try {
    const h = window.location.hostname;
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '[::1]' ||
      h.endsWith('.local') ||
      h.endsWith('.localhost')
    );
  } catch {
    return false;
  }
}

/** One entry in the ordered room-host failover chain (V2 Phase A / M106). */
export interface RoomHost {
  key: string;
  url: string;
  /** V2 Phase C (M108): the host's WebTransport datagram endpoint (an https
   *  origin whose QUIC the room server terminates itself with our own cert —
   *  Fly's proxy can't carry WT). Absent = no lane (the Cloudflare fallback,
   *  and any future host without UDP). */
  wtUrl?: string;
}

/**
 * The room hosts (V2 Phase B, M107): three regional Fly apps + the Cloudflare
 * DO as the terminal SAFETY NET. Entry 0 ('us') is the static default for
 * anything unrouted (bare dev joins, legacy 4-char codes) — what matters is
 * that the choice is IDENTICAL on every client, not where it points. Real
 * traffic is routed explicitly: the matchmaker hands out the continent's host
 * key, friend-room creators pick by latency probe, and invite links/codes pin
 * the room's host (`&h=us` / the code's 5th letter) — an allowlist lookup,
 * never a raw URL, so a crafted link can only ever point at OUR hosts (M100).
 *
 * FAILOVER: any regional host that can't be reached on a fresh connect falls
 * back to 'cf' (nextRoomHostUrl) — a Fly regional incident degrades players
 * to the old Cloudflare infrastructure, not to an error screen. Same rule on
 * every client → everyone converges on the same host per room.
 */
export const ROOM_HOSTS: RoomHost[] = [
  { key: 'us', url: 'wss://dr-rooms-us.fly.dev', wtUrl: 'https://rooms-us.dribbleracer.com:4433' },
  { key: 'eu', url: 'wss://dr-rooms-eu.fly.dev', wtUrl: 'https://rooms-eu.dribbleracer.com:4433' },
  {
    key: 'asia',
    url: 'wss://dr-rooms-asia.fly.dev',
    wtUrl: 'https://rooms-asia.dribbleracer.com:4433',
  },
  { key: 'cf', url: networkConfig.wsUrl },
];

/** The terminal fallback — the old Cloudflare referee, kept deployed + wire-
 *  identical (one RoomCore, two shells). */
const FALLBACK_KEY = 'cf';

export const defaultRoomHost = (): RoomHost => ROOM_HOSTS[0];

export const roomHostByKey = (key: string): RoomHost | undefined =>
  ROOM_HOSTS.find((h) => h.key === key);

/** The regional hosts a friend-room creator latency-probes (the fallback is
 *  never a preferred target — it's where you land when a region is down). */
export const probeableRoomHosts = (): RoomHost[] =>
  ROOM_HOSTS.filter((h) => h.key !== FALLBACK_KEY);

/** Which chain entry a full ws URL belongs to (null = a dev ?host= override). */
export const roomHostKeyFor = (url: string): string | null =>
  ROOM_HOSTS.find((h) => url.startsWith(h.url))?.key ?? null;

/** The same URL (query intact) on the FALLBACK host, or null when the URL is
 *  already on it (chain exhausted) / is a dev override outside the table. */
export const nextRoomHostUrl = (currentUrl: string): string | null => {
  const cur = ROOM_HOSTS.find((h) => currentUrl.startsWith(h.url));
  if (!cur || cur.key === FALLBACK_KEY) return null;
  const fb = roomHostByKey(FALLBACK_KEY);
  return fb ? fb.url + currentUrl.slice(cur.url.length) : null;
};
